"use client";

import { cn } from "@gettactic/helpers";
import { X } from "lucide-react";
import React from "react";
import { Button } from "../../primitives/button";
import { ResourcePickerControlProps } from "./ResourcePickerFieldInput.types";

/**
 * The picker control itself (no label/error chrome): a dashed "pick" button when empty,
 * or a bordered row with the selection plus "Change"/remove actions when filled. Shared
 * by seat/location-style fields that pick a value through an external picker (e.g. a modal).
 */
export const ResourcePickerControl: React.FC<ResourcePickerControlProps> = ({
  isSelected,
  selectedLabel,
  icon,
  placeholder,
  onOpen,
  onClear,
  disabled = false,
  clearLabel = "Remove selection",
  className
}) => {
  if (isSelected) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-md border border-neutral-200 px-3 py-2.5",
          className
        )}
      >
        <span className="flex shrink-0 text-neutral-400">{icon}</span>
        <span className="min-w-0 flex-1 truncate text-neutral-700 text-sm">
          {selectedLabel}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onOpen}
          disabled={disabled}
        >
          Change
        </Button>
        <button
          type="button"
          aria-label={clearLabel}
          onClick={onClear}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-md border border-neutral-200 border-dashed px-3 py-3 text-neutral-500 text-sm transition-colors",
        disabled
          ? "cursor-not-allowed opacity-60"
          : "hover:border-neutral-300 hover:text-neutral-700",
        className
      )}
    >
      {icon}
      {placeholder}
    </button>
  );
};
